<template>
	<div class="flex flex-col w-full justify-between rounded-xl p-4 shadow bg-gradient-to-t from-ihh-purple via-ihh-pink to-ihh-pink" :product-id="product.id">
		<div class="flex flex-col w-full flex-grow">
			<div class="flex space-y-2 flex-grow">
				<div class="flex items-center justify-center mr-4 sm:mr-6 w-full max-w-[25%] sm:max-w-32 sm:max-h-32 aspect-square rounded-xl p-3 shadow bg-ihh-white">
					<img class="max-w-full max-h-full object-center object-contain aspect-square" :src="currentImgSrc" @error="onImgError" />
				</div>
				<div class="flex flex-col w-full space-y-2">
					<div>
						<div class="flex text-sm text-ihh-midnight-gray font-semibold float-right pl-2 pb-2">
							<button @click="showDetails = true" class="flex scale-75 sm:scale-100">
								<InfoIcon />
								<span class="text-ihh-dark-salmon hover:text-ihh-salmon underline text-xs font-semibold">Details</span>
							</button>
						</div>
						<router-link :to="`/products${product.customUrl?.url}`">
							<div class="text-sm sm:text-base font-semibold text-ihh-midnight-gray break-words">{{ product.name }}</div>
						</router-link>
						<div v-if="productOutOfStock" class="mt-1 mb-1 px-2 py-1 rounded bg-ihh-coral text-white text-xs text-center font-medium inline-block w-auto">
							Out of stock
						</div>
					</div>
					<div class="text-xs md:text-sm text-ihh-slate-gray capitalize font-thin">
						{{ currentPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
						<span v-if="selectedVariant?.optionValues?.length"> / {{ selectedVariant.optionValues?.map((ov) => ov.label)?.join(', ') }}</span>
					</div>

					<div class="w-full space-y-2">
						<div class="space-y-2 flex flex-wrap w-full" v-for="po in product.productOptions" :key="po.id">
							<div class="text-ihh-slate-gray font-semibold text-xs">
								<span class="text-ihh-midnight-gray mr-2 opacity-80">{{ po.displayName }}:</span>
							</div>
							<div class="flex w-full flex-wrap gap-2">
								<button
									v-for="o in po.optionValues"
									:key="o.id"
									@click="selectOption(po.id, o)"
									class="border-2 rounded-lg px-2 py-1 text-xs transition-all"
									:class="{
										'ring ring-ihh-midnight-gray bg-ihh-slate-gray/20 border-transparent font-semibold': isOptionSelected(o),
										'border-ihh-gray/50 bg-ihh-slate-gray/20 hover:border-ihh-midnight-gray/50': !isOptionSelected(o),
									}"
								>
									{{ o.label }}
								</button>
								<button
									v-if="hasOptionSelected(po.id)"
									@click="clearOption(po.id)"
									class="border-2 rounded-lg px-2 py-1 text-xs transition-all border-ihh-coral/50 text-ihh-coral hover:border-ihh-coral hover:bg-ihh-coral/10"
								>
									Clear
								</button>
							</div>
						</div>
					</div>

					<div class="flex justify-between items-center pt-2">
						<div v-if="!optional" class="flex items-center text-ihh-midnight-gray space-x-3 text-sm">
							<button
								@click="decreaseQuantity"
								class="shadow shadow-ihh-midnight-gray/20 flex w-5 h-5 p-1 rounded-full bg-white items-center justify-center cursor-pointer"
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
									<path d="M19 13H5v-2h14v2z" />
								</svg>
							</button>
							<div class="font-semibold">{{ quantity }}</div>
							<button
								@click="increaseQuantity"
								class="shadow shadow-ihh-midnight-gray/20 flex w-5 h-5 p-1 rounded-full bg-white items-center justify-center cursor-pointer"
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
									<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
								</svg>
							</button>
						</div>
						<button @click="$emit('remove')" class="px-3 py-2 rounded bg-ihh-dark-salmon text-white text-xs">Remove</button>
					</div>
				</div>
			</div>
		</div>

		<Modal v-if="showDetails" @hide="showDetails = false" maxWidth="max-w-xl">
			<div class="prose">
				<h2 class="text-center">Product Details</h2>
				<div v-safe-html="product.description" />
			</div>
		</Modal>
	</div>
</template>

<script>
import InfoIcon from '../layout/info-icon.vue'
import Modal from '../layout/modal.vue'

export default {
	components: { Modal, InfoIcon },
	props: ['product', 'variant', 'quantity', 'optional'],
	data() {
		return {
			showDetails: false,
			selectedOptions: {},
			currentImgSrc: null,
			isInternalUpdate: false,
		}
	},
	mounted() {
		this.currentImgSrc = this.thumbnail
		this.initializeSelectedOptions()
	},
	watch: {
		variant: {
			handler(newVal, oldVal) {
				if (newVal?.id !== oldVal?.id && !this.isInternalUpdate) {
					this.initializeSelectedOptions()
				}
			},
			immediate: true,
		},
		purchaseIncrement(newIncrement, oldIncrement) {
			// When switching to a variant sold in a different increment, re-snap the quantity to a valid multiple.
			if (newIncrement === oldIncrement) return
			const inc = newIncrement
			let n = Math.round((Number(this.quantity) || inc) / inc) * inc
			if (n < inc) n = inc
			if (n !== this.quantity) this.$emit('update:quantity', n)
		},
	},
	methods: {
		initializeSelectedOptions() {
			this.selectedOptions = {}
			if (this.variant?.optionValues?.length) {
				for (let ov of this.variant.optionValues) {
					let po = this.product.productOptions?.find((p) => p.optionValues?.find((o) => o.id === ov.id))
					if (po) {
						this.selectedOptions[po.id] = ov.id
					}
				}
			}
		},
		selectOption(productOptionId, option) {
			this.selectedOptions = { ...this.selectedOptions, [productOptionId]: option.id }
			this.findAndEmitVariant()
		},
		isOptionSelected(option) {
			return Object.values(this.selectedOptions).includes(option.id)
		},
		hasOptionSelected(productOptionId) {
			return this.selectedOptions[productOptionId] !== undefined
		},
		clearOption(productOptionId) {
			let newOptions = { ...this.selectedOptions }
			delete newOptions[productOptionId]
			this.selectedOptions = newOptions
			this.findAndEmitVariant()
		},
		findAndEmitVariant() {
			let selectedOptionIds = Object.values(this.selectedOptions)
			let variant = this.product?.variants?.find((v) => v.optionValues?.every((ov) => selectedOptionIds.includes(ov.id)))
			this.isInternalUpdate = true
			this.$emit('update:variant', variant?.id || null)
			this.$nextTick(() => {
				this.isInternalUpdate = false
			})
		},
		increaseQuantity() {
			this.$emit('update:quantity', this.quantity + this.purchaseIncrement)
		},
		decreaseQuantity() {
			if (this.quantity > this.purchaseIncrement) {
				this.$emit('update:quantity', this.quantity - this.purchaseIncrement)
			}
		},
		onImgError() {
			this.currentImgSrc = '/images/obDefaultProductImage-min.jpg'
		},
	},
	computed: {
		thumbnail() {
			return this.product?.images?.find((i) => i?.isThumbnail)?.url || this.product?.images?.[0]?.url || '/images/obDefaultProductImage-min.jpg'
		},
		selectedVariant() {
			let selectedOptionIds = Object.values(this.selectedOptions)
			if (!selectedOptionIds.length) return this.variant
			return this.product?.variants?.find((v) => v.optionValues?.every((ov) => selectedOptionIds.includes(ov.id))) || this.variant
		},
		currentPrice() {
			return this.selectedVariant?.currentPrice || this.product?.currentPrice || this.selectedVariant?.calculatedPrice || this.product?.calculatedPrice
		},
		productOutOfStock() {
			if (this.product?.inventoryTracking === 'variant') {
				return this.product?.variants?.every((v) => v.inventoryLevel <= 0)
			}
			return this.product?.outOfStock || !this.product?.isVisible
		},
		purchaseIncrement() {
			// Required purchase increment: the selected variant's wins, falling back to the product's, then 1.
			const variantInc = Number(this.variant?.purchaseIncrement)
			if (Number.isFinite(variantInc) && variantInc > 0) return variantInc
			const productInc = Number(this.product?.purchaseIncrement)
			if (Number.isFinite(productInc) && productInc > 0) return productInc
			return 1
		},
	},
}
</script>

<style scoped>
button,
div {
	-webkit-tap-highlight-color: transparent !important;
}
</style>
